import { getCurrentInstance, inject, ref } from 'vue'
import {
  accessCodes,
  type Agency,
  type AgencyStructureNode
} from '@gat-ship/types'
import { agencyKey, agencyStructureKey } from '@gat-ship/types/injection-keys'
import type {
  AgencyCommands,
  AgencyService
} from '@gat-ship/types/injection-keys'
import { useAgencyApiCalls } from '@gat-ship/ref-data'
import {
  useAgencySignalREvents,
  useCompanySignalREvents
} from '@gat-ship/signalr'
import { useUser } from '@gat-ship/user'
import { globalUtils } from '@gat-ship/utils'
import { mapAgencyToAgencyStructureNode } from '../model/agency-structure-utils'

/**
 * Creates a reactive single-agency data source with caching,
 * request deduplication, and SignalR live-sync.
 */
export function useAgency(): AgencyService {
  const {
    getAgencyRequest,
    changeAgencyNameRequest,
    changeAgencyContactInfoRequest,
    moveAgencyRequest,
    linkAgencyToAnotherCompanyRequest,
    deactivateAgencyRequest,
    reactivateAgencyRequest
  } = useAgencyApiCalls()

  const user = useUser()
  const isLoading = ref(false)
  const _agency = ref<Agency>()

  /**
   * The agency node is the node in the agency structure that the agency belongs to.
   * It is primarily used to determine the actions that are available for the agency.
   */
  const _agencyNode = ref<AgencyStructureNode>()

  function setData(agency?: Agency) {
    _agency.value = agency
    _agencyNode.value = agency
      ? mapAgencyToAgencyStructureNode(agency)
      : undefined
  }

  /** Returns a new copy of the agency if found in the data. */
  function findAgencyById(agencyId?: string): Agency | undefined {
    if (!agencyId) {
      return undefined
    }
    if (_agency.value?.id === agencyId) {
      return { ..._agency.value }
    }
    return undefined
  }

  function updateAgency(agencyId: string, agency: Agency) {
    if (_agency.value && _agency.value.id === agencyId) {
      setData({ ...agency })
    }
  }

  const agencySignalREvents = useAgencySignalREvents({
    onAgencyMoved: (event) => {
      const { agencyId } = event.message
      if (agencyId === _agency.value?.id) {
        fetchAgency(agencyId, { forceFetch: true })
      }
    },
    onAgencyDeactivated: (event) => {
      const { agencyId } = event.message
      if (agencyId === _agency.value?.id) {
        fetchAgency(agencyId, { forceFetch: true })
      }
    },
    onAgencyReactivated: (event) => {
      const { agencyId } = event.message
      if (agencyId === _agency.value?.id) {
        fetchAgency(agencyId, { forceFetch: true })
      }
    },
    onAgencyNameChanged: (event) => {
      const { agencyId, name, shortName } = event.message
      const agency = findAgencyById(agencyId)
      if (agency) {
        agency.name = name
        agency.shortName = shortName
        updateAgency(agencyId, agency)
      }
    },
    onAgencyContactInfoChanged: async (event) => {
      const { agencyId } = event.message
      if (agencyId === _agency.value?.id) {
        fetchAgency(agencyId, { forceFetch: true })
      }
    },
    onAgencyLinkedToNewCompany: async (event) => {
      const { agencyId } = event.message
      if (agencyId === _agency.value?.id) {
        fetchAgency(agencyId, { forceFetch: true })
      }
    }
  })

  const companySignalREvents = useCompanySignalREvents({
    onCompanyDetailsChanged: (event) => {
      const { code, name, description } = event.message
      const agency = findAgencyById(_agency.value?.id)
      if (
        agency &&
        agency.linkedCompany &&
        agency.linkedCompany.code === code
      ) {
        agency.linkedCompany.name = name
        agency.linkedCompany.description = description ?? undefined
        updateAgency(agency.id, agency)
      }
    }
  })

  function unsubscribeFromEvents() {
    agencySignalREvents.unsubscribeFromAgencyEvents()
    companySignalREvents.unsubscribeFromCompanyEvents()
  }

  function subscribeToEvents() {
    agencySignalREvents.subscribeToAgencyEvents()
    companySignalREvents.subscribeToCompanyEvents()

    return () => {
      unsubscribeFromEvents()
    }
  }

  /**
   * Fetches the agency. If the agency is already loaded, it will not be fetched again unless forceFetch is true.
   * @param agencyId - The id of the agency to fetch.
   * @param options.forceFetch - Whether to force a fetch of the agency. @default false
   * @param options.subscribeToEvents - Whether to subscribe to agency events. @default true
   */
  async function fetchAgency(
    agencyId: string,
    options?: { forceFetch: boolean; subscribeToEvents?: boolean }
  ) {
    if (
      !user.hasAgencyPermissions(
        accessCodes.administration.agency.view,
        agencyId
      )
    ) {
      return
    }
    if (!options?.forceFetch && agencyId === _agency.value?.id) {
      // if the agency is already loaded we can presume that it's up-to-date.
      return
    }
    isLoading.value = true
    const result = await getAgencyRequest(agencyId)
    if (result?.agency) {
      setData(result.agency)
    }
    if (result) {
      isLoading.value = false
      return
    }
    if (options?.subscribeToEvents ?? true) {
      subscribeToEvents()
    }
    isLoading.value = false
    return _agency.value
  }

  // The agency-structure tree is a sibling datasource provided by an ancestor
  // AgencyStructureProvider (see the agency route layout). Capturing it here at
  // setup lets commands keep the tree in sync after a mutation — orchestration
  // every agency modal previously had to duplicate. Optional: not every mount
  // site has a structure provider above it.
  const agencyStructure = getCurrentInstance()
    ? inject(agencyStructureKey, undefined)
    : undefined

  /** Force-refetches the single agency and, when present, the structure tree. */
  async function refetchAfterMutation(agencyId: string) {
    await fetchAgency(agencyId, { forceFetch: true })
    await agencyStructure?.fetchAgencyStructure({ forceFetch: true })
  }

  /**
   * Runs a mutation request, then refreshes the agency + structure. Wraps the
   * whole cycle in the shared `isLoading` flag so the write is observable.
   */
  async function runCommand(
    agencyId: string,
    request: (commandId: string) => Promise<unknown>
  ) {
    isLoading.value = true
    try {
      await request(globalUtils.functions.uuid())
      await refetchAfterMutation(agencyId)
    } finally {
      isLoading.value = false
    }
  }

  const commands: AgencyCommands = {
    changeName: (agencyId, payload) =>
      runCommand(agencyId, (commandId) =>
        changeAgencyNameRequest(agencyId, payload, commandId)
      ),
    changeContactInfo: (agencyId, payload) =>
      runCommand(agencyId, (commandId) =>
        changeAgencyContactInfoRequest(agencyId, payload, commandId)
      ),
    move: (agencyId, payload) =>
      runCommand(agencyId, (commandId) =>
        moveAgencyRequest(agencyId, payload, commandId)
      ),
    linkToCompany: (agencyId, payload) =>
      runCommand(agencyId, (commandId) =>
        linkAgencyToAnotherCompanyRequest(agencyId, payload, commandId)
      ),
    deactivate: (agencyId) =>
      runCommand(agencyId, (commandId) =>
        deactivateAgencyRequest(agencyId, { commandId }, commandId)
      ),
    reactivate: (agencyId, payload) =>
      runCommand(agencyId, (commandId) =>
        reactivateAgencyRequest(agencyId, payload, commandId)
      )
  }

  return {
    agency: _agency,
    agencyNode: _agencyNode,
    findAgencyById,
    fetchAgency,
    updateAgency,
    commands,
    /** Clears the store and unsubscribes from agency events.
     * @param options.unsubscribeFromEvents - Whether to unsubscribe from agency events. @default true
     */
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      setData(undefined)
      if (options?.unsubscribeFromEvents ?? true) {
        unsubscribeFromEvents()
      }
    },
    isLoading
  }
}

/** Inject the single-agency context provided by a parent AgencyProvider. */
export function useAgencyContext() {
  return inject(agencyKey, undefined)
}
